import Button from '@mui/material/Button';
import { useState } from 'react';
import { sandhillsRequest, gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import { gtmEventType, mfaDisplayModes } from '@Components/Utilities/SandhillsConstants';
import translator from '@Components/Utilities/Translator';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import MultiFactorContent from '@Components/Shared/MultiFactorContent';
import PaymentAuthorizationForm from '@Components/Forms/PaymentAuthorization/PaymentAuthorizationForm';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useSetRecoilState, useRecoilValue } from 'recoil';

const BidderRegistration = (props) => {

    const [hasPassedCCAuthentication, setHasPassedCCAuthentication] = useState(props.HasPassedCCAuthentication);

    const [multifactorAuthenticationID, setMultifactorAuthenticationID] = useState(false);
    const [hasPassedMultiFactorAuthentication, setHasPassedMultiFactorAuthentication] = useState(props.HasPassedMultiFactorAuthentication);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState(props?.ValidationErrors?.Error);

    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);

    const onPaymentMethodAuthorized = (authTransactionID) => {
        onStepCompleted({ AuthTransactionID: authTransactionID });
    };

    const onMultiFactorAuthorized = (multifactorAuthID) => {
        onStepCompleted({ MultiFactorAuthenticationID: multifactorAuthID });
    };

    const onStepCompleted = (data) => {
        setLoading(true);
        setErrors(null);

        sandhillsRequest({
            url: routes.BidderRegistrationPost,
            method: 'post',
            data: data,
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            if (data) {
                let errors = data.ValidationErrors || {};
                if (!data.Success && !errors?.Error)
                    errors.Error = translator.translate('sandhills.errortryagain');

                if (errors?.Error) {
                    setErrors(errors.Error);
                }
                else if (data.RedirectURL) {
                    gtmPushEvent(gtmEventType.bidderRegistrationSubmit);
                    window.location.href = props.ToURL ?? data.RedirectURL;
                }
                else {
                    // Successfully saved CC Auth and/or MFA
                    setHasPassedCCAuthentication(data.HasPassedCCAuthentication);
                    setHasPassedMultiFactorAuthentication(data.HasPassedMultiFactorAuthentication);
                    setMultifactorAuthenticationID(data.MultiFactorAuthenticationID);
                }
            }
            else {
                setErrors(translator.translate('sandhills.errortryagain'));
            }
            setLoading(false);
        });
    };

    const renderRegistrationContent = () => {
        if (errors) {
            return (
                <div className="center red-text"><strong>{errors}</strong></div>
            );
        }
        else if (!props.IsRevoked && props.PassedDuplicateContactCheck) {
            if (!hasPassedCCAuthentication)
                return authorizationContent();
            else if (!hasPassedMultiFactorAuthentication)
                return multiFactorContent();
        }

        return bidderRegistrationMessageContent();
    };

    const authorizationContent = () => {
        return (
            <div className={currentSite?.Site?.IsAuctionSite && props.NewRegistration ? 'registration__container' : ''} >
                <PaymentAuthorizationForm
                    {...props}
                    PreAuthAmountText={translator.translate('Sandhills.RegistrationCreditCardAuthAuctioneerDisclosure', [props.FormalDomainName, props.FormalDomainName, props.SupportTollFreePhone])}
                    DealerCRMID={props.SandhillsGlobalPaymentsCRMID}
                    IsBidderRegistration={true}
                    onPaymentMethodAuthorized={onPaymentMethodAuthorized} />
            </div>
        );
    };

    const multiFactorContent = () => {
        if (loading) {
            return (
                <LoadingRing sxBox={{ 'justify-content': 'center' }} />
            );
        } else {
            return (
                <MultiFactorContent
                    {...props}
                    onMultiFactorAuthorized={onMultiFactorAuthorized}
                    displayMode={mfaDisplayModes.BidderRegistration}
                    forceNewMFA={true}
                    textLoading={false}
                    textVerificationDisabled={!props.MultifactorAuthenticationValues.PhoneNumbers.some(phone => phone.CanSendTexts)}
                    flaggedAsFrequentNewBidder={props.FlaggedAsFrequentNewBidder}
                />
            );
        }
    };

    const bidderRegistrationMessageContent = () => {
        if (props.IsRevoked) {
            return (
                <div className={currentSite?.Site?.IsAuctionSite && props.NewRegistration ? 'registration__container bidder-registration-message-container' : 'bidder-registration-message-container'}>
                    <div>
                        <p><strong>{props.Firstname}, {translator.translate('sandhills.tradepubwebsite.mvc.revokedstatustext')}</strong></p>
                        <p>{translator.translate('sandhills.tradepubwebsite.mvc.speaktoarepresentative')}:</p>
                        <p>{translator.translate('sandhills.tradepubwebsite.mvc.numbersandhoursstatustext', [props.SupportTollFreePhone, props.SupportPhone])}</p>
                        <p>{translator.translate('sandhills.tradepubwebsite.mvc.officehours')}</p>
                    </div>
                    {completedButton()}
                </div>
            );
        } else if (!hasPassedMultiFactorAuthentication || multifactorAuthenticationID == 0) {
            return (
                <div className={currentSite?.Site?.IsAuctionSite && props.NewRegistration ? 'registration__container bidder-registration-message-container' : 'bidder-registration-message-container'}>
                    {!props.PassedDuplicateContactCheck ?
                        <>
                            <h2 className="green">{translator.translate('sandhills.thankyouforregistering')}</h2>
                            <div className="matching-bidder-info-message-box">
                                <i className="fas fa-exclamation-triangle"></i> <strong>{translator.translate('sandhills.matchingbiddingregistration')}</strong>
                            </div>
                        </>
                        :
                        <>
                            <h2 className="green">
                                {translator.translate('sandhills.tradepubwebsite.mvc.thankyouforsubmitting')}<br />
                                {translator.translate('sandhills.wewillcallyouatthenumberprovided')} 
                            </h2>
                            <div className="instant-verification">
                                <strong>{translator.translate('sandhills.phonenumberverificationregistrationinstructions')}</strong>
                            </div>
                            <div>
                                <Button variant="contained" onClick={() => { setHasPassedMultiFactorAuthentication(false); setSelectedTextMessage(true); }} className={'template-branded-button ' + (currentSite?.Site?.IsAuctionSite ? 'ts-button' : '')}>
                                    {translator.translate('sandhills.verifybytextmessage')}
                                </Button>
                            </div>
                        </>}
                    <h2>{translator.translate('sandhills.tradepubwebsite.mvc.importantpleaseread').toUpperCase()}</h2>
                    <div>
                        <span>{translator.translate('sandhills.registrationinformationbeingreviewed')}</span><br />
                        <span dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.checktofindoutwhenapproved', [props.CanIBidLink, props.FormalDomainName]) }} />.&nbsp;
                        <span>{translator.translate('sandhills.tradepubwebsite.mvc.noneedtoregistermultipletimes')}</span><br />
                        <span>{translator.translate('sandhills.tradepubwebsite.mvc.bidderconfirmationemailmessage')}</span>
                    </div>
                    {questionsAndOfficeHours()}
                    {thankYouAgain()}
                    {completedButton()}
                </div>
            );
        }

        return (
            <div className={currentSite?.Site?.IsAuctionSite && props.NewRegistration ? 'registration__container bidder-registration-message-container' : 'bidder-registration-message-container'}>
                <h2 className="green">
                    {translator.translate('sandhills.congratulationsyouareapproved')}<br />
                    {translator.translate('sandhills.youcannowbidon', [props.FormalDomainName])}
                </h2>
                {currentSite?.Site?.IsAuctionTime && <div className="have-items-to-sell">
                    <strong>{translator.translate('sandhills.informationaboutauctioningequipment')}</strong><br/>
                    <span dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.learnhowweconnectyouandauctioneers', [props.FormalDomainName, props.ListingInputLink, props.FormalDomainName]) }} />
                </div>}
                {questionsAndOfficeHours()}
                {thankYouAgain()}
                {completedButton()}
            </div>
        );
    };

    const questionsAndOfficeHours = () => {
        return (
            <div className={currentSite?.Site?.IsAuctionSite && props.NewRegistration ? 'registration__container questions-and-office-hours' : 'questions-and-office-hours'}>            
                <strong>{translator.translate('sandhills.tradepubwebsite.mvc.questions?')}</strong>
                <br />
                {translator.translate('sandhills.contactyourrepresentative', [props.FormalDomainName, props.SupportTollFreePhone, props.SupportPhone])}
                {translator.translate('sandhills.tradepubwebsite.mvc.officehours')}
            </div>
        );
    };

    const thankYouAgain = () => {
        if (props.PassedDuplicateContactCheck && hasPassedMultiFactorAuthentication) {
            return (
                <div className="thank-you">
                    {translator.translate('sandhills.tradepubwebsite.mvc.thankyouagainforsubmission')}
                </div>
            );
        }
        return (<></>);
    };

    const completedButton = () => {
        return (
            <Button onClick={() => { window.location.href = props.ToURL; }} className={'template-branded-button ' + (currentSite?.Site.IsBidcaller ? 'ts-button' : '')}>
                <strong>{currentSite?.Site?.IsAuctionSite && props.NewRegistration ? translator.translate('sandhills.setupyourauctionalerts') : translator.translate('sandhills.continue')}</strong>
            </Button>
        );
    };

    return (
        renderRegistrationContent()
    );
};

export default BidderRegistration;